import React from "react";
import { Box, Card, Tooltip, Typography } from "@mui/material";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TrendIndicator from "./TrendIndicator";
import { unitLabel } from "../utils/performanceTrends";

// Ported verbatim from startup-web (views/performance/components/MetricSummaryCard.js).
const ACCENT = "#6938EF";

const iconForUnit = (unit) => {
    switch (unit) {
        case "INR":
            return <CurrencyRupeeRoundedIcon />;
        case "USD":
            return <AttachMoneyRoundedIcon />;
        case "Percentage":
            return <PercentRoundedIcon />;
        case "Count":
            return <GroupsOutlinedIcon />;
        case "Months":
            return <AccessTimeRoundedIcon />;
        default:
            return <ShowChartRoundedIcon />;
    }
};

// Summary card shown above the Data table ("plain") and above the charts grid
// ("chart" — adds a leading icon and a sparkline). One card per configured metric.
const MetricSummaryCard = ({ metric, variant = "plain", selected = false, onClick }) => {
    const { name, unit, value, change, prevMonth, points = [], slug } = metric;
    const suffix = prevMonth ? `vs ${prevMonth}` : null;
    const isChart = variant === "chart";
    // Unique gradient id per card so the light-purple area fill renders independently.
    const gradientId = `spark-gradient-${slug || name}`.replace(/[^a-zA-Z0-9_-]/g, "-");

    return (
        <Card
            onClick={onClick}
            sx={{
                boxShadow: "none",
                borderRadius: "12px",
                border: `1px solid ${selected ? ACCENT : "#EAECF0"}`,
                background: selected ? "#F9F5FF" : "#FFFFFF",
                padding: { xs: "0.875rem 1rem", md: "1rem 1.25rem" },
                height: "100%",
                cursor: onClick ? "pointer" : "default",
                transition: "border-color 0.15s ease, background 0.15s ease",
            }}
        >
            {isChart ? (
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: { xs: 1.25, md: 1 } }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, md: 1 }, mb: { xs: 0.5, md: 1 }, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: { xs: 26, md: 30 },
                                    height: { xs: 26, md: 30 },
                                    borderRadius: { xs: "7px", md: "8px" },
                                    background: "#F4EBFF",
                                    color: ACCENT,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    "& svg": { fontSize: { xs: 15, md: 17 } },
                                }}
                            >
                                {iconForUnit(unit)}
                            </Box>
                            {/* Truncate rather than wrap: a long metric name must never grow the
                                text column into the sparkline sitting beside it. */}
                            <Typography
                                title={`${name} ${unitLabel(unit)}`.trim()}
                                sx={{
                                    fontSize: { xs: "0.75rem", md: "0.8rem" },
                                    fontWeight: 500,
                                    color: "#475467",
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {name} {unitLabel(unit)}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.4rem" }, fontWeight: 700, color: "#101828", lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {value}
                        </Typography>
                        <Box sx={{ mt: { xs: 0.4, md: 0.75 } }}>
                            <TrendIndicator change={change} suffix={suffix} />
                        </Box>
                    </Box>
                    <Box sx={{ width: { xs: 84, sm: 62, md: 92, lg: 62, xl: 92 }, height: 52, flexShrink: 0, marginBlock: "auto" }}>
                        {points.length > 1 && (
                            <>
                                {/* light-purple → transparent vertical gradient for the area fill */}
                                <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden>
                                    <defs>
                                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={ACCENT} stopOpacity={0.28} />
                                            <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <SparkLineChart
                                    data={points}
                                    height={52}
                                    area
                                    curve="natural"
                                    colors={[ACCENT]}
                                    showHighlight
                                    margin={{ top: 4, bottom: 4, left: 0, right: 0 }}
                                    sx={{
                                        "& .MuiAreaElement-root": { fill: `url(#${gradientId})` },
                                        "& .MuiLineElement-root": { strokeWidth: 2 },
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#475467" }}>
                            {name}
                        </Typography>
                        <Tooltip title={`${name} ${unitLabel(unit)}`.trim()}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#98A2B3", flexShrink: 0 }} />
                        </Tooltip>
                    </Box>
                    <Typography sx={{ fontSize: "1.9rem", fontWeight: 700, color: "#101828", lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#667085", mt: 0.5, mb: 1 }}>
                        {unitLabel(unit) ? unitLabel(unit).replace(/[()]/g, "") : " "}
                    </Typography>
                    <TrendIndicator change={change} suffix={suffix} />
                </>
            )}
        </Card>
    );
};

export default MetricSummaryCard;
